import React, { useState, useEffect } from 'react';
import { Quote, ChevronLeft, ChevronRight, Star } from 'lucide-react';

// Historias anonimizadas pero realistas. Usan ciudades reales de Chile para contextualizar.
var TESTIMONIALS = [
  {
    quote: 'Encontré un centro residencial para mi hermano en solo dos días. El filtro por región y el chatbot me ayudaron a comparar opciones sin perder tiempo. Ya lleva 8 meses limpio.',
    name: 'Carolina M.',
    context: 'Familiar · Santiago',
    addiction: 'Pasta base',
    rating: 5
  },
  {
    quote: 'Después de 15 años con alcohol no sabía por dónde empezar. Acá encontré un psiquiatra especializado en patología dual y un grupo de apoyo cerca de mi casa. Sigo en tratamiento y hoy tengo esperanza.',
    name: 'Rodrigo P.',
    context: 'Paciente · Valparaíso',
    addiction: 'Alcohol + depresión',
    rating: 5
  },
  {
    quote: 'Como centro de rehabilitación, publicar nuestra ficha fue clave para llegar a más familias. En 3 meses triplicamos las consultas. El plan Elite nos permitió incluir nuestras sucursales en Concepción y Chillán.',
    name: 'Dr. Esteban H.',
    context: 'Director clínico · Biobío',
    addiction: 'Centro residencial',
    rating: 5
  },
  {
    quote: 'Busqué por horas en Google hasta que llegué a este directorio. La calculadora me ayudó a entender el costo real del tratamiento y la guía de cómo elegir centro me dio la confianza para tomar la decisión.',
    name: 'Paulina R.',
    context: 'Familiar · Temuco',
    addiction: 'Cocaína',
    rating: 5
  },
  {
    quote: 'Llegué al directorio por un taller de meditación. Terminé encontrando a mi terapeuta actual y un grupo de NA presencial. La plataforma es un ecosistema completo, no solo un listado.',
    name: 'Matías V.',
    context: 'Paciente · Rancagua',
    addiction: 'Marihuana',
    rating: 5
  }
];

export default function TestimonialsCarousel() {
  var [idx, setIdx] = useState(0);
  var [paused, setPaused] = useState(false);

  useEffect(function() {
    if (paused) return;
    var timer = setInterval(function() {
      setIdx(function(i) { return (i + 1) % TESTIMONIALS.length; });
    }, 6500);
    return function() { clearInterval(timer); };
  }, [paused]);

  function prev() { setIdx(function(i) { return (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length; }); }
  function next() { setIdx(function(i) { return (i + 1) % TESTIMONIALS.length; }); }

  var t = TESTIMONIALS[idx];

  return (
    <section className="py-20 md:py-24 bg-slate-950 relative overflow-hidden" data-testid="testimonials-carousel">
      {/* Subtle background glow */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/5 blur-3xl rounded-full"></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Historias reales</p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-50">Personas que encontraron ayuda aquí</h2>
          <p className="text-slate-400 mt-3 text-sm md:text-base">Pacientes, familiares y centros que han usado nuestro directorio.</p>
        </div>

        <div
          className="relative"
          onMouseEnter={function() { setPaused(true); }}
          onMouseLeave={function() { setPaused(false); }}
        >
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-900/40 border border-slate-800 rounded-3xl p-6 md:p-12 relative overflow-hidden min-h-[300px] md:min-h-[260px]" data-testid="testimonial-card">
            <Quote className="absolute top-6 left-6 w-16 h-16 text-emerald-500/10" aria-hidden="true" />

            <div key={idx} className="animate-in fade-in slide-in-from-bottom-2 duration-500">
              {/* Stars */}
              <div className="flex gap-1 mb-5 justify-center md:justify-start">
                {Array.from({ length: t.rating }).map(function(_, i) {
                  return <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />;
                })}
              </div>

              <blockquote className="text-lg md:text-xl text-slate-200 leading-relaxed mb-6 italic" data-testid="testimonial-quote">
                "{t.quote}"
              </blockquote>

              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="font-semibold text-slate-50">{t.name}</p>
                  <p className="text-sm text-slate-400">{t.context}</p>
                </div>
                <span className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-xs font-medium">
                  {t.addiction}
                </span>
              </div>
            </div>
          </div>

          {/* Navigation arrows */}
          <button
            onClick={prev}
            aria-label="Testimonio anterior"
            data-testid="testimonial-prev"
            className="absolute left-0 md:-left-4 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-1/2 w-10 h-10 rounded-full bg-slate-900 border border-slate-700 hover:border-emerald-500 hover:text-emerald-400 text-slate-400 flex items-center justify-center transition-all shadow-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            aria-label="Siguiente testimonio"
            data-testid="testimonial-next"
            className="absolute right-0 md:-right-4 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-1/2 w-10 h-10 rounded-full bg-slate-900 border border-slate-700 hover:border-emerald-500 hover:text-emerald-400 text-slate-400 flex items-center justify-center transition-all shadow-lg"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-8" role="tablist">
          {TESTIMONIALS.map(function(_, i) {
            return (
              <button
                key={i}
                onClick={function() { setIdx(i); }}
                aria-label={'Ver testimonio ' + (i + 1)}
                data-testid={'testimonial-dot-' + i}
                className={'h-2 rounded-full transition-all ' + (i === idx ? 'w-8 bg-emerald-400' : 'w-2 bg-slate-700 hover:bg-slate-600')}
              ></button>
            );
          })}
        </div>

        {/* Schema.org Review markup for SEO */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "SinAdicciones.org",
          "url": "https://sinadicciones.org",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": TESTIMONIALS.length,
            "bestRating": "5"
          },
          "review": TESTIMONIALS.map(function(tt) {
            return {
              "@type": "Review",
              "reviewRating": { "@type": "Rating", "ratingValue": tt.rating, "bestRating": 5 },
              "author": { "@type": "Person", "name": tt.name },
              "reviewBody": tt.quote
            };
          })
        }) }} />
      </div>
    </section>
  );
}
